import RichTextEditor, { RichTextArea } from "..";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { isHotkey } from "is-hotkey";
import { debounce } from "lodash";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FieldRenderProps } from "react-final-form";
import scrollIntoView from "scroll-into-view-if-needed";
import { Descendant, Editor, Element, Transforms } from "slate";
import { withHistory } from "slate-history";
import { ReactEditor, withReact } from "slate-react";

import { DesignToken, FieldError } from "@livechat/design-system-react-components";

import { SizeExceededText } from "components/message-box/richtext/MessageBox";
import { EventPlace } from "helpers/analytics";
import { shouldDisplayError } from "helpers/final-form";
import { numberSort } from "helpers/sort";
import { useAttachments } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import TicketAttachments, { IUploadingAttachment } from "routes/tickets/ticket/message-box/richtext/TicketAttachments";
import { actions$ } from "store/action-stream";
import { RequestAction } from "store/entities/actions";
import { TRANSACTION } from "store/entities/transaction/actions";
import { TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE } from "store/entities/transaction/constants";
import {
  ITransactionUploadAttachmentFailurePayload,
  ITransactionUploadAttachmentSuccessPayload,
  TransactionType,
} from "store/entities/transaction/interfaces";
import { IAction, IActionWithPayload } from "store/helper";
import { withVar } from "themes/helpers";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import RichTextEditorToolbar from "../RichTextEditorToolbar";
import LinkUI, { IUpdateParams } from "../link-ui";
import { PluginSpec } from "../plugins";
import BlockSoftBreaksPlugin from "../plugins/blockSoftBreaks";
import { RichTextEditorPluginProps, RichTextPluginProps, RichTextVariables } from "../plugins/constants";
import HTMLCopyPastePlugin from "../plugins/htmlCopyPaste";
import ImagesPlugin from "../plugins/images";
import LinksPlugin from "../plugins/links";
import ListBreaksPlugin from "../plugins/listBreaks";
import OnelineHeadersPlugin from "../plugins/onelineHeaders";
import RichTextPlugin from "../plugins/richText";
import SlateCopyPastePlugin from "../plugins/slateCopyPaste";
import VariablesPlugin from "../plugins/variables";

import { ContentTextAreaContainer } from "./styles";

export type IVariableSelectParams = {
  label: string;
  content: string;
};

export type IFormattedTextEditorProps = FieldRenderProps<Descendant[], HTMLDivElement> &
  RichTextEditorPluginProps &
  RichTextPluginProps & {
    variablesPluginOptions?: RichTextVariables[];
    handleVariableSelect?: (editor: Editor) => (params: IVariableSelectParams) => void;
    insertCleanVariables?: boolean;
    placeholder?: string;
    eventPlace?: EventPlace;
    toolbarWrap?: boolean;
    disabled?: boolean;
    withDebounce?: boolean;
    richTextAreaBackgroundColor?: string;
    richTextAreaMinHeight?: string;
    richTextAreaMaxHeight?: string;
    transactionType?: TransactionType;
    cannedResponseID?: string;
  };

export const FormattedTextEditor: FC<IFormattedTextEditorProps> = props => {
  const {
    eventPlace,
    placeholder,
    input,
    disabled,
    toolbarWrap,
    withDebounce,
    transactionType,
    cannedResponseID,
    handleVariableSelect,
    meta,
    ...selectedPlugins
  } = props;
  const { linksPlugin, richTextPlugin, variablesPlugin, imagesPlugin } = selectedPlugins;
  const [uploadingAttachments, setUploadingAttachments] = useState<{
    [key: string]: IUploadingAttachment & {
      order: number;
    };
  }>({});
  const { abortUpload, mergeTransaction, attachmentsSize } = useAttachments();
  const [linkUpdateParams, setLinkUpdateParams] = useState<IUpdateParams | null>(null);
  const richEditor = useRef<Editor | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const plugins = useRef<PluginSpec[]>([
    [withHistory, {}],
    ...(linksPlugin ? [LinksPlugin] : []),
    ...(imagesPlugin ? [ImagesPlugin] : []),
    ...(richTextPlugin ? [RichTextPlugin] : []),
    ...(variablesPlugin ? [VariablesPlugin] : []),
    OnelineHeadersPlugin,
    BlockSoftBreaksPlugin,
    SlateCopyPastePlugin,
    HTMLCopyPastePlugin,
    ListBreaksPlugin,
    [withReact, {}],
  ]);

  const transactionSizeExceeded = attachmentsSize() > TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  const handleLinkInsert = (event: any) => {
    const editor = richEditor.current;
    if (!editor) return;

    const [linkEntry] = Editor.nodes(editor, {
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      match: (node: any) => Element.isElement(node) && node.type === "link",
      universal: true,
    });

    let linkRange = null;
    if (linkEntry) {
      linkRange = Editor.range(editor, Editor.start(editor, linkEntry[1]), Editor.end(editor, linkEntry[1]));
      Transforms.select(editor, linkRange);
    }

    event.preventDefault();
    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, []));
    }

    if (editor.selection) {
      const domRange = ReactEditor.toDOMRange(editor, editor.selection);
      if (domRange?.startContainer?.parentElement) {
        scrollIntoView(domRange?.startContainer?.parentElement, { scrollMode: "if-needed" });
      }
    }

    setLinkUpdateParams({
      // @ts-expect-error TS(2345)
      selection: Editor.rangeRef(editor, editor.selection, { affinity: "inward" }),
      ...(linkRange ? { focusDisplayName: false } : { focusDisplayName: true }),
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleLinkHotkey = useCallback((event: any, pluginKeyDown: any) => {
    const isLinkHotkey = isHotkey("mod+k");

    if (isLinkHotkey(event.nativeEvent)) {
      handleLinkInsert(event);
    }

    pluginKeyDown(event);
  }, []);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleRemoveUpload = (localId: string) => {
    setUploadingAttachments(prev => {
      const newUploads = { ...prev };
      delete newUploads[localId];

      return newUploads;
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleAbortUpload = (localId: string) => {
    abortUpload(localId);
    handleRemoveUpload(localId);
  };

  useEffect(() => {
    if (cannedResponseID && transactionType === "cannedResponse") {
      mergeTransaction({ cannedResponseID });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      // @ts-expect-error TS(2345)
      filter((action: IAction) =>
        [
          TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.SUCCESS],
          TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.FAILURE],
        ].includes(action.type)
      ),
      subscribe(
        (
          action:
            | IActionWithPayload<string, ITransactionUploadAttachmentSuccessPayload>
            | IActionWithPayload<string, ITransactionUploadAttachmentFailurePayload>
        ) => {
          const { localID } = action.payload;
          handleRemoveUpload(localID);
        }
      )
    );

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <div ref={containerRef}>
      {transactionSizeExceeded && <SizeExceededText>Your message cannot exceed 10 MB</SizeExceededText>}
      <RichTextEditor
        plugins={plugins.current}
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onInit={(editor: any) => {
          // eslint-disable-next-line no-param-reassign
          editor.eventPlace = eventPlace;
          richEditor.current = editor;
        }}
        value={input.value as unknown as Descendant[]}
        onChange={withDebounce ? debounce(input.onChange, 500) : input.onChange}
      >
        <ContentTextAreaContainer
          // FIXME: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={(event: any) => {
            event.preventDefault();
            if (richEditor.current) {
              ReactEditor.focus(richEditor.current);
            }
          }}
          minHeight={props.richTextAreaMinHeight}
          maxHeight={props.richTextAreaMaxHeight}
          backgroundColor={disabled ? withVar(DesignToken.SurfacePrimaryDisabled) : props.richTextAreaBackgroundColor}
          borderColor={
            transactionSizeExceeded ? withVar(DesignToken.BorderBasicNegative) : withVar(DesignToken.BorderBasicPrimary)
          }
        >
          {selectedPlugins.attachmentsPlugin && (
            <TicketAttachments
              // @ts-expect-error TS(2322)
              editor={richEditor.current}
              privateMode={false}
              onAbortUpload={handleAbortUpload}
              uploadingAttachments={Object.values(uploadingAttachments).sort((a1, a2) =>
                numberSort(a1.order, a2.order)
              )}
            />
          )}
          <LinkUI
            params={linkUpdateParams}
            setParams={setLinkUpdateParams}
            containerRef={containerRef.current}
            eventPlace={eventPlace}
          />
          <RichTextArea
            id="rich-text-area"
            onKeyDown={handleLinkHotkey}
            onBlur={input.onBlur}
            placeholder={placeholder}
            disabled={disabled}
          />
        </ContentTextAreaContainer>
        <RichTextEditorToolbar
          // @ts-expect-error TS(2322)
          toolbarWrap={toolbarWrap}
          // @ts-expect-error TS(2322)
          disabled={disabled}
          onLinkInsert={handleLinkInsert}
          // @ts-expect-error TS(2322)
          setUploadingAttachments={setUploadingAttachments}
          uploadingAttachments={uploadingAttachments}
          // @ts-expect-error TS(2322)
          handleVariableSelect={handleVariableSelect}
          {...selectedPlugins}
        />
        {shouldDisplayError(meta) ? (
          <FieldError>
            <span dangerouslySetInnerHTML={{ __html: props.meta.error }} />
          </FieldError>
        ) : undefined}
      </RichTextEditor>
    </div>
  );
};
